/* eslint-disable no-param-reassign */
import axios from 'axios';
import { keycloak } from '../keycloak';
import { refreshAccessToken } from './API/auth';

const baseConfig = {
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {},
};

const request = axios.create(baseConfig);

// Flag per tracciare se stiamo già facendo un refresh
let isRefreshing = false;
let refreshSubscribers = [];
let refreshRejectCallbacks = [];
let isLoggingOut = false;

const clearStoredTokens = () => {
  localStorage.removeItem('kc_token');
  localStorage.removeItem('kc_refreshToken');
  localStorage.removeItem('kc_idToken');
};

const forceReauth = () => {
  if (isLoggingOut) {
    return;
  }
  isLoggingOut = true;
  clearStoredTokens();
  if (keycloak?.authenticated) {
    keycloak.logout({ redirectUri: window.location.origin });
  } else {
    keycloak?.login?.({ redirectUri: window.location.origin });
  }
};

// Sottoscrivi i refresh token per retry le richieste in coda
const subscribeTokenRefresh = (cb, rejectCb) => {
  refreshSubscribers.push(cb);
  if (rejectCb) refreshRejectCallbacks.push(rejectCb);
};

const onRefreshed = (token) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
  refreshRejectCallbacks = [];
};

const onRefreshFailed = (error) => {
  refreshRejectCallbacks.forEach((cb) => cb(error));
  refreshSubscribers = [];
  refreshRejectCallbacks = [];
};

// Helper per aspettare che il token sia disponibile
const waitForToken = () =>
  new Promise((resolve) => {
    let attempts = 0;
    const maxAttempts = 50; // 50 * 100ms = 5 secondi
    const checkToken = () => {
      if (keycloak?.token || attempts >= maxAttempts) {
        resolve();
      } else {
        attempts += 1;
        setTimeout(checkToken, 100);
      }
    };
    checkToken();
  });

/**
 * Intercetta le requests per aggiungere il token e fare il refresh se necessario
 */
request.interceptors.request.use(async (req) => {
  // Aspetta che Keycloak sia inizializzato
  await waitForToken();

  // Controlla se il token è scaduto o sta per scadere (entro 60 secondi)
  if (keycloak?.token) {
    const isTokenExpired = keycloak.isTokenExpired(60);

    if (isTokenExpired && keycloak.refreshToken) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          // Fai il refresh del token tramite il nostro endpoint
          const tokenResponse = await refreshAccessToken(keycloak.refreshToken);

          // Aggiorna i token di Keycloak
          keycloak.token = tokenResponse.access_token;
          keycloak.refreshToken = tokenResponse.refresh_token;

          // Salva i token nel localStorage
          localStorage.setItem('kc_token', tokenResponse.access_token);
          localStorage.setItem('kc_refreshToken', tokenResponse.refresh_token);

          // Aggiorna anche il tokenParsed se necessario
          if (tokenResponse.access_token) {
            try {
              const base64Url = tokenResponse.access_token.split('.')[1];
              const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
              const jsonPayload = decodeURIComponent(
                atob(base64)
                  .split('')
                  .map(
                    (c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`
                  )
                  .join('')
              );
              keycloak.tokenParsed = JSON.parse(jsonPayload);
            } catch (e) {
              console.warn('Failed to parse refreshed token:', e);
            }
          }

          isRefreshing = false;
          onRefreshed(tokenResponse.access_token);
        } catch (error) {
          isRefreshing = false;
          onRefreshFailed(error);
          console.warn(
            'Token refresh failed, will continue with existing token:',
            error
          );
          // forceReauth() intentionally NOT called here.
          // If the token is truly expired the server will return 401,
          // which is handled by the response interceptor.
        }
      } else {
        // Se è già in corso un refresh, aspetta che finisca
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh(
            () => {
              req.headers.Authorization = `Bearer ${keycloak?.token}`;
              resolve(req);
            },
            (error) => reject(error)
          );
        });
      }
    }
  }

  req.headers.Authorization = `Bearer ${keycloak?.token}`;
  return req;
});

request.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config;
    if (status === 401 && !originalRequest?.handled401) {
      if (originalRequest) {
        originalRequest.handled401 = true;
      }
      forceReauth();
    }
    return Promise.reject(error);
  }
);

export const initAuth = (auth, login, logout) => {
  if (auth?.authenticated) {
    /* eslint-disable camelcase */
    const {
      given_name,
      family_name,
      email,
      preferred_username,
      resource_access,
    } = auth.tokenParsed;
    login(
      {
        first_name: given_name,
        last_name: family_name,
        email,
        username: preferred_username,
      },
      resource_access
    );
    /* eslint-enable camelcase */
  } else {
    logout();
  }
};

export default request;
